import React, { useEffect, useState, useCallback } from 'react';
import Button from './Button';
import {
  ReactFlow,
  Node,
  Edge,
  Connection,
  useNodesState,
  useEdgesState,
  Background,
  Controls,
  MiniMap,
  NodeTypes,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import WorkflowNode from './WorkflowNode';
import FlowStepEditor from './FlowStepEditor';
import { 
  convertToReactFlowFormat, 
  calculateHierarchicalLayout 
} from '../utils/workflowConverter';

interface FlowStep {
  stepId: string;
  fieldName: string;
  question: string;
  type: 'text' | 'email' | 'phone' | 'number' | 'select';
  validation: {
    required: boolean;
    type: string;
  };
  errorMessage?: string;
  options?: Array<{
    id: string;
    label: string;
    value: string;
  }>;
  dependsOn?: string;
  showWhen?: string;
}

interface CompanyFlowBuilderProps {
  flowSteps: FlowStep[];
  onAddStep: (step: FlowStep) => void;
  onUpdateStep: (stepId: string, step: FlowStep) => void;
  onDeleteStep: (stepId: string) => void;
  onConnectionsChange?: (connections: Array<{ source: string; target: string }>) => void;
}

const nodeTypes: NodeTypes = {
  step: WorkflowNode,
};

const CompanyFlowBuilder: React.FC<CompanyFlowBuilderProps> = ({
  flowSteps,
  onAddStep,
  onUpdateStep,
  onDeleteStep,
  onConnectionsChange,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [showNewStepForm, setShowNewStepForm] = useState(false);

  // Sincronizar flowSteps con React Flow usando el converter
  useEffect(() => {
    console.log('========================================');
    console.log('🔄 CompanyFlowBuilder - Syncing flowSteps');
    console.log('📊 flowSteps:', flowSteps);
    console.log('========================================');

    if (!flowSteps || flowSteps.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    // Convertir formato BD a ReactFlow
    const { steps, connections } = convertToReactFlowFormat(flowSteps);
    
    console.log('✅ Conversion complete:', {
      steps: steps.length,
      connections: connections.length,
    });

    // Calcular posiciones con layout jerárquico
    const positions = calculateHierarchicalLayout(steps, connections);

    // Crear nodos de ReactFlow
    const flowNodes: Node[] = steps.map((step) => ({
      id: step.stepId,
      data: {
        label: step.question,
        type: step.type,
        fieldName: step.fieldName,
        isConditional: !!step.dependsOn,
        isSelected: selectedStepId === step.stepId,
      },
      position: positions[step.stepId] || { x: 0, y: 0 },
      type: 'step',
    }));

    // Crear edges de ReactFlow
    const flowEdges: Edge[] = connections.map((conn) => ({
      id: conn.id,
      source: conn.sourceStepId,
      target: conn.targetStepId,
      label: conn.label || conn.condition || '',
      type: 'smoothstep',
      animated: true,
      style: { 
        stroke: '#94a3b8', 
        strokeWidth: 2 
      },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#94a3b8',
        width: 20,
        height: 20,
      },
      labelStyle: {
        fontSize: 11,
        fontWeight: 500,
      },
      labelBgStyle: {
        fill: '#ffffff',
      },
    }));

    console.log('🎨 ReactFlow elements created:');
    console.log('  - Nodes:', flowNodes.length);
    console.log('  - Edges:', flowEdges.length);
    console.log('========================================');

    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowSteps, selectedStepId, setNodes, setEdges]);

  const handleNodeClick = (nodeId: string) => {
    setSelectedStepId(nodeId);
    setShowNewStepForm(false);
  };

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (connection.source && connection.target) {
        console.log('🔗 New connection:', connection);
        
        // Notificar al padre sobre los cambios
        if (onConnectionsChange) {
          onConnectionsChange([{ source: connection.source, target: connection.target }]);
        }
      }
    },
    [onConnectionsChange]
  );

  const handleAddStep = (step: FlowStep) => {
    onAddStep(step);
    setShowNewStepForm(false);
  };

  const handleUpdateStep = (stepId: string, updates: FlowStep) => {
    onUpdateStep(stepId, updates);
    setSelectedStepId(null);
  };

  const handleDeleteStep = (stepId: string) => {
    onDeleteStep(stepId);
    if (selectedStepId === stepId) {
      setSelectedStepId(null);
    }
  };

  const selectedStep = flowSteps.find(s => s.stepId === selectedStepId);

  return (
    <div className="flex flex-col md:flex-row h-full gap-0 md:gap-4">

      {/* ── Panel de pasos — barra compacta en mobile, panel lateral en desktop ── */}
      <div className={`md:w-96 bg-brand-light border-b md:border-b-0 md:border-r border-brand-border flex flex-col overflow-hidden md:rounded-lg md:border flex-shrink-0
        ${(showNewStepForm || selectedStep) ? 'h-auto max-h-[50vh]' : 'h-auto'} md:h-full md:max-h-none`}>

        {/* Header — siempre visible */}
        <div className="px-3 py-2 md:p-4 border-b border-brand-border bg-brand-surface flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-bold text-xs md:text-lg text-gray-900 truncate">Pasos del Flujo</h3>
            <p className="text-[10px] md:text-xs text-gray-500">{flowSteps.length} pasos · {edges.length} conexiones</p>
          </div>
          {/* Botón agregar — siempre visible en el header en mobile */}
          <Button variant="primary" size="xs"
            onClick={() => { setShowNewStepForm(true); setSelectedStepId(null); }}
          >
            + Agregar
          </Button>
        </div>

        {/* Editor — solo visible cuando hay algo que mostrar */}
        {(showNewStepForm || selectedStep) && (
          <div className="flex-1 overflow-y-auto p-3 md:p-4">
            {showNewStepForm ? (
              <div className="bg-brand-surface p-3 rounded-lg border border-brand-yellow/40">
                <FlowStepEditor
                  onSave={handleAddStep}
                  onCancel={() => setShowNewStepForm(false)}
                />
              </div>
            ) : selectedStep ? (
              <div className="bg-brand-surface p-3 rounded-lg border border-brand-yellow/40">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-sm text-gray-900">Editar Paso</h4>
                  <button
                    onClick={() => {
                      if (window.confirm('¿Eliminar este paso?')) handleDeleteStep(selectedStep.stepId);
                    }}
                    className="text-red-600 hover:text-red-800 text-xs font-medium px-2 py-1 hover:bg-red-50 rounded transition"
                  >
                    Eliminar
                  </button>
                </div>
                <FlowStepEditor
                  step={selectedStep}
                  onSave={(step) => handleUpdateStep(selectedStep.stepId, step)}
                  onCancel={() => setSelectedStepId(null)}
                />
              </div>
            ) : null}
          </div>
        )}

        {/* Placeholder desktop cuando no hay selección */}
        {!showNewStepForm && !selectedStep && (
          <p className="hidden md:block text-center py-6 text-sm text-gray-400 px-4">
            Selecciona un paso en el canvas o usa "+ Agregar"
          </p>
        )}
      </div>

      {/* ── Panel derecho: canvas ReactFlow ──────────────────────── */}
      <div className="flex-1 bg-brand-surface rounded-lg border border-brand-border overflow-hidden flex flex-col
        min-h-[350px] md:h-full">
        {/* Tip */}
        <div className="bg-brand-yellow/10 border-b border-brand-yellow/30 px-3 py-1.5 text-xs text-brand-dark">
          Los conectores se generan automáticamente desde los campos "dependsOn" y "showWhen"
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-hidden">
          {flowSteps.length === 0 ? (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <p className="text-sm">No hay pasos configurados</p>
                <p className="text-xs mt-1">Crea el primer paso para comenzar</p>
              </div>
            </div>
          ) : (
            <ReactFlow
              nodes={nodes.map(node => ({
                ...node,
                data: {
                  ...node.data,
                  onClick: () => handleNodeClick(node.id),
                  onDelete: () => handleDeleteStep(node.id),
                },
              }))}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={handleConnect}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              minZoom={0.3}
              maxZoom={2}
              proOptions={{ hideAttribution: true }}
            >
              <Background color="#e5e7eb" gap={16} />
              <Controls />
              <MiniMap
                nodeColor={(node) => {
                  const type = node.data.type as string;
                  const colors: Record<string, string> = {
                    text: '#3b82f6', email: '#8b5cf6', phone: '#ec4899',
                    number: '#f59e0b', select: '#10b981',
                  };
                  return colors[type] || '#6b7280';
                }}
                maskColor="rgba(0, 0, 0, 0.1)"
              />
            </ReactFlow>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyFlowBuilder;
